import { useAuth0 } from '@auth0/auth0-react';
import { IUserApiEntity } from 'data/Api/User';
import {
	FC,
	PropsWithChildren,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { DI } from '../../shared/di';
import { useAuthContext } from '../Auth/Auth.hooks';
import { IUserContext, UserContext } from './User.context';
import {
	generateUserPermissionLanguages,
	generateUserPermissions,
} from './User.utils';
import { AppPaths } from '../App/App.constant';
import { generatePath, useNavigate } from 'react-router-dom';

export const UserProvider: FC<PropsWithChildren> = ({ children }) => {
	const GetSelfUserUseCase = DI.resolve('GetSelfUserUseCase');
	const GetManyUsersUseCase = DI.resolve('GetManyUsersUseCase');

	const [users, setUsers] = useState<IUserApiEntity[]>([]);
	const [currentAccount, setCurrentAccount] = useState<IUserApiEntity | null>(
		null,
	);
	const [currentLanguage, setCurrentLanguage] = useState<string>('');
	const [showCellNumbers, setShowCellNumbers] = useState<boolean>(false);

	const { isAuthenticated } = useAuth0();
	const { currentToken } = useAuthContext();
	const navigate = useNavigate();

	const localStorageCurrentLanguage = localStorage.getItem('current_language');

	const languageSwitcherUrl = generatePath(AppPaths.languageSwitcher);

	/**
	 * Get the current user from the API
	 */
	const getSelfUser = useCallback(async () => {
		const user = await GetSelfUserUseCase.execute();
		setCurrentAccount(user ?? null);
	}, []);

	useEffect(() => {
		if (isAuthenticated && currentToken) {
			getSelfUser();
		}
	}, [isAuthenticated, currentToken]);

	const accountPermissions = useMemo(
		() => generateUserPermissions(currentAccount),
		[currentAccount],
	);

	const accountLanguages = useMemo(
		() => generateUserPermissionLanguages(currentAccount),
		[currentAccount],
	);

	const setDefaultCurrentLanguage = useCallback(
		(language: string) => {
			localStorage.setItem('current_language', `${language}`);
			setCurrentLanguage(language);
		},
		[setCurrentLanguage],
	);

	useEffect(() => {
		if (currentLanguage) return;

		if (!!localStorageCurrentLanguage) {
			setCurrentLanguage(localStorageCurrentLanguage);
		} else if (accountLanguages.length) {
			if (accountLanguages.length > 1) {
				navigate(languageSwitcherUrl);
			} else {
				setDefaultCurrentLanguage(accountLanguages[0]);
			}
		}
	}, [accountLanguages, currentLanguage, localStorageCurrentLanguage]);

	/**
	 * Get default value for showCellNumbers based on language
	 * Denmark: true, all others: false
	 */
	const getDefaultShowCellNumbers = useCallback((language: string): boolean => {
		return language === 'dk' || language === 'se';
	}, []);

	/**
	 * Initialize showCellNumbers from localStorage or set default based on language
	 */
	useEffect(() => {
		if (!currentLanguage) return;

		const storedPref = localStorage.getItem('show_cell_numbers');

		if (storedPref !== null) {
			setShowCellNumbers(storedPref === 'true');
		} else {
			setShowCellNumbers(getDefaultShowCellNumbers(currentLanguage));
		}
	}, [currentLanguage, getDefaultShowCellNumbers]);

	/**
	 * Wrapper for setShowCellNumbers that persists to localStorage
	 */
	const handleSetShowCellNumbers = useCallback((show: boolean) => {
		localStorage.setItem('show_cell_numbers', show.toString());
		setShowCellNumbers(show);
	}, []);

	/**
	 * Get all users from the API
	 */
	useEffect(() => {
		const getAllUsers = async () => {
			const users = await GetManyUsersUseCase.execute({
				pageSize: 100,
			});
			setUsers(users);
		};

		if (currentAccount?.id && currentLanguage && !users.length) {
			getAllUsers();
		}
	}, [users, currentAccount, currentLanguage]);

	/**
	 * Create the context value
	 */
	const UserContextValue: IUserContext = useMemo(
		() => ({
			currentAccount,
			accountPermissions,
			accountLanguages,
			currentLanguage,
			setCurrentLanguage: setDefaultCurrentLanguage,
			users,
			showCellNumbers,
			setShowCellNumbers: handleSetShowCellNumbers,
		}),
		[
			currentAccount,
			accountPermissions,
			users,
			accountLanguages,
			currentLanguage,
			showCellNumbers,
			handleSetShowCellNumbers,
		],
	);

	return (
		<UserContext.Provider value={UserContextValue}>
			{children}
		</UserContext.Provider>
	);
};
